/**
 * Client API « Mes Tickets » (lien magique acheteur) — backend Mboapass.
 */

import { getPublicApiBaseUrl } from './publicEventsApi';

const BUYER_TOKEN_KEY = 'mboapass_buyer_token';
const BUYER_EMAIL_KEY = 'mboapass_buyer_email';

export function getBuyerToken() {
  try {
    return localStorage.getItem(BUYER_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function getBuyerEmail() {
  try {
    return localStorage.getItem(BUYER_EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

export function setBuyerSession({ token, email }) {
  try {
    if (token) localStorage.setItem(BUYER_TOKEN_KEY, token);
    if (email) localStorage.setItem(BUYER_EMAIL_KEY, email);
  } catch {
    /* ignore */
  }
}

export function clearBuyerSession() {
  try {
    localStorage.removeItem(BUYER_TOKEN_KEY);
    localStorage.removeItem(BUYER_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

async function parseJsonResponse(res) {
  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const nested = json?.error?.message;
    const msg =
      (Array.isArray(nested) ? nested.join(', ') : null) ||
      (typeof nested === 'string' ? nested : null) ||
      (Array.isArray(json?.message) ? json.message.join(', ') : null) ||
      (typeof json?.message === 'string' ? json.message : null) ||
      res.statusText ||
      'Erreur réseau';
    const err = new Error(typeof msg === 'string' ? msg : 'Erreur réseau');
    err.status = res.status;
    throw err;
  }
  return json;
}

function extractData(json) {
  const outer = json?.data;
  if (
    outer &&
    typeof outer === 'object' &&
    !Array.isArray(outer) &&
    'data' in outer &&
    outer.meta === undefined
  ) {
    return outer.data;
  }
  return outer !== undefined ? outer : json;
}

/** Demande l’envoi d’un lien magique. 404 si aucun achat PAID pour cet email. */
export async function requestBuyerMagicLink(email) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/buyer/auth/request-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const json = await parseJsonResponse(res);
  return extractData(json);
}

/** Consomme le token one-shot et renvoie { token, email, expiresAt }. */
export async function consumeBuyerMagicLink(token) {
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}/buyer/auth/consume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const json = await parseJsonResponse(res);
  return extractData(json);
}

async function buyerFetch(path) {
  const token = getBuyerToken();
  if (!token) {
    const err = new Error('Session expirée');
    err.status = 401;
    throw err;
  }
  const base = getPublicApiBaseUrl();
  const res = await fetch(`${base}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const json = await parseJsonResponse(res);
  return extractData(json);
}

export async function fetchBuyerMe() {
  return buyerFetch('/buyer/me');
}

export async function fetchBuyerTickets() {
  return buyerFetch('/buyer/tickets');
}

export async function fetchBuyerInstallments() {
  return buyerFetch('/buyer/installments');
}

/**
 * Télécharge le PDF des billets d’une commande (même rendu que l’email).
 * @param {string} orderId
 */
export async function downloadBuyerOrderTicketsPdf(orderId) {
  const token = getBuyerToken();
  if (!token) {
    const err = new Error('Session expirée');
    err.status = 401;
    throw err;
  }
  const base = getPublicApiBaseUrl();
  const res = await fetch(
    `${base}/buyer/orders/${encodeURIComponent(orderId)}/tickets-pdf`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!res.ok) {
    let msg = 'Impossible de télécharger le PDF';
    try {
      const json = await res.json();
      const nested = json?.error?.message || json?.message;
      if (typeof nested === 'string') msg = nested;
      else if (Array.isArray(nested)) msg = nested.join(', ');
    } catch {
      /* ignore */
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `billets-mboapass.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * @param {string} orderId
 * @param {{ paymentMethod: string, phone?: string, settleRemaining?: boolean }} body
 */
export async function payNextBuyerInstallment(orderId, body) {
  const token = getBuyerToken();
  if (!token) {
    const err = new Error('Session expirée');
    err.status = 401;
    throw err;
  }
  const base = getPublicApiBaseUrl();
  const res = await fetch(
    `${base}/buyer/orders/${encodeURIComponent(orderId)}/pay-next`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  const json = await parseJsonResponse(res);
  return extractData(json);
}

export function qrImageUrl(data, size = 180) {
  const q = encodeURIComponent(String(data || ''));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${q}`;
}
